import React, { useState, useEffect, useRef, startTransition, useCallback, useMemo } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import {
  tasksAtom,
  fetchTasksAtom,
  updateTaskAtom,
  tasksLoadingAtom
} from '../../atoms/v0/taskAtoms';
import {
  newTaskProjectIdAtom,
  taskViewModeAtom,
  showCompletedTasksAtom,
  TaskViewMode,
  globalTaskEditAtom,
  GlobalTaskEditState
} from '../../atoms/v3/taskAtoms';
import {
  projectsAtom,
  fetchProjectsAtom,
  updateProjectAtom,
  createProjectAtom
} from '../../atoms/v0/projectAtoms';
import {
  sortedProjectsAtom,
  sortedTimeGroupsAtom,
  visibleProjectIdsAtom,
  visibleTimeGroupsAtom
} from '../../atoms/v3/outlineAtoms';
import { Task } from '../../types/task';
import { Project } from '../../types/project';
import {
  TaskList,
  TaskSearchBar,
  DraggableTaskButton,
  TaskDndContext,
  DroppableArea,
  DroppableAreaType,
  ImportanceCategory,
  TimeGroupCategory
} from './components/tasks';
import { ProjectActionButton } from './components/projects/ProjectActionButton';
import { TasksNavigationBar } from './components/tasks/TasksNavigationBar';
import { CreateTaskParams, UpdateTaskParams } from './components/tasks/TaskDndContext';
import { TaskDetailSheet } from './components/tasks/TaskDetailSheet';
import { ProjectEditModal } from './components/projects/ProjectEditModal';
import { ProjectMergeModal } from './components/projects/ProjectMergeModal';
import { ProjectService } from '../../services/projectService';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../hooks/v0/useThemeMode';
import { IOSSegmentedControl } from './components/ios';
import { addDays, startOfDay, endOfDay } from 'date-fns';
import { ListViewIcon, ListViewHiddenIcon } from './components/ios/IOSIcons';
import { motion, AnimatePresence, useScroll } from 'framer-motion';
import { groupTasksByTime, groupTasksByProject } from '../../utils/taskUtils';

// 使用从taskAtoms导入的TaskViewMode类型

export const Tasks: React.FC = () => {
  const { t } = useTranslation(['v3/common', 'v3/tasks']);
  const navigate = useNavigate();
  const { isDarkMode } = useThemeMode();
  const containerRef = useRef<HTMLDivElement>(null);
  const initialLoadRef = useRef<boolean>(false);

  // 滚动动画
  const { scrollY } = useScroll({
    container: containerRef
  });

  // 状态
  const [tasks, setTasks] = useAtom(tasksAtom);
  const [projects, setProjects] = useAtom(projectsAtom);
  const [, fetchTasks] = useAtom(fetchTasksAtom);
  const [, fetchProjects] = useAtom(fetchProjectsAtom);
  const [, updateTask] = useAtom(updateTaskAtom);
  const [, updateProject] = useAtom(updateProjectAtom);
  const [, createProject] = useAtom(createProjectAtom);

  // 共享的排序后的项目列表和分组信息
  const [, setSortedProjects] = useAtom(sortedProjectsAtom);
  const [, setSortedTimeGroups] = useAtom(sortedTimeGroupsAtom);
  const [, setVisibleProjectIds] = useAtom(visibleProjectIdsAtom);
  const [, setVisibleTimeGroups] = useAtom(visibleTimeGroupsAtom);

  // 项目编辑模态框状态
  const [editingProject, setEditingProject] = useState<{
    id: string;
    title: string;
    description: string;
    folderId?: string | null;
  } | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // 项目合并模态框状态
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [isLoading] = useAtom(tasksLoadingAtom);
  const [searchQuery, setSearchQuery] = useState('');

  // 任务详情浮层状态
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);
  const [newTaskInitialProjectId, setNewTaskInitialProjectId] = useState<string | undefined>(undefined);
  const [newTaskImportance, setNewTaskImportance] = useState<ImportanceCategory | undefined>(undefined);
  const [newTaskTimeGroup, setNewTaskTimeGroup] = useState<TimeGroupCategory | undefined>(undefined);

  // 拖放状态
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);

  // 显示已完成任务状态 - 使用持久化存储
  const [showCompleted, setShowCompleted] = useAtom(showCompletedTasksAtom);

  // 视图模式状态 - 使用持久化存储
  const [viewMode, setViewMode] = useAtom(taskViewModeAtom);

  // Jotai atom for pre-selecting project (might still be useful elsewhere or as fallback)
  const [, setNewTaskProjectIdAtom] = useAtom(newTaskProjectIdAtom);

  // 全局任务编辑状态
  const [globalTaskEdit, setGlobalTaskEdit] = useAtom(globalTaskEditAtom);

  // 加载任务和项目
  useEffect(() => {
    // Only fetch on initial mount to prevent unnecessary fetches
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      // Wrap data fetching in startTransition to prevent blocking UI
      startTransition(() => {
        fetchTasks();
        fetchProjects();
      });
    }
  }, [fetchTasks, fetchProjects]);

  // 监听全局任务编辑状态
  useEffect(() => {
    if (globalTaskEdit.isOpen && globalTaskEdit.taskId && globalTaskEdit.triggerSource === 'hyperlink') {
      // 设置选中的任务ID并打开任务详情
      setSelectedTaskId(globalTaskEdit.taskId);
      setIsTaskDetailOpen(true);

      // 重置全局状态
      setGlobalTaskEdit({
        taskId: null,
        isOpen: false,
        triggerSource: 'normal'
      });
    }
  }, [globalTaskEdit, setGlobalTaskEdit]);

  // 当 tasks 或 projects 更新时，更新带有项目信息的任务 - 使用 useMemo
  const tasksWithProjects = useMemo(() => {
    // 创建项目映射表，用于显示项目名称
    const projectsMap = projects.reduce((acc, project) => {
      acc[project.id] = project;
      return acc;
    }, {} as Record<string, Project>);

    return tasks.map(task => {
      if (task.projectId && projectsMap[task.projectId]) {
        return {
          ...task,
          project: {
            id: projectsMap[task.projectId].id,
            title: projectsMap[task.projectId].title
          }
        };
      }
      return task;
    });
  }, [tasks, projects]);

  // 过滤任务 - 使用 useMemo
  const filteredTasks = useMemo(() => tasksWithProjects.filter(task => {
    // 首先检查任务对象和标题是否存在
    if (!task || !task.title) {
      console.warn(t('v3/tasks:errors.invalidTask'), task);
      return false;
    }

    // 根据搜索查询过滤
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (task.notes && task.notes.toLowerCase().includes(searchQuery.toLowerCase()));

    // 然后根据完成状态过滤
    // 如果不显示已完成任务，则过滤掉已完成的任务
    const matchesCompletionStatus = showCompleted ? true : !task.done;

    return matchesSearch && matchesCompletionStatus;
  }), [tasksWithProjects, searchQuery, showCompleted]);

  // 按类别分类任务 - 四象限（重要且紧急、重要、紧急、不重要不紧急）
  // 这些也可以用 useMemo 包裹，如果它们在其他地方作为依赖或 props
  const vitalUrgentTasks = useMemo(() => filteredTasks.filter(task =>
    task.vital && task.urgent && !task.done
  ), [filteredTasks]);

  const vitalTasks = useMemo(() => filteredTasks.filter(task =>
    task.vital && !task.urgent && !task.done
  ), [filteredTasks]);

  const urgentTasks = useMemo(() => filteredTasks.filter(task =>
    !task.vital && task.urgent && !task.done
  ), [filteredTasks]);

  const otherTasks = useMemo(() => filteredTasks.filter(task =>
    !task.vital && !task.urgent && !task.done
  ), [filteredTasks]);

  const completedTasks = useMemo(() => filteredTasks.filter(task => task.done), [filteredTasks]);

  // 按时间分组任务 - 使用 useMemo
  const tasksByTime = useMemo(() => groupTasksByTime(filteredTasks), [filteredTasks]);

  // 按项目分组任务 - 使用 useMemo
  const tasksByProject = useMemo(() => groupTasksByProject(filteredTasks), [filteredTasks]);

  // 当 tasks 或 projects 更新时，更新带有项目信息的任务
  // useEffect(() => { // This useEffect is now redundant due to useMemo for tasksWithProjects
  //   // 创建项目映射表，用于显示项目名称
  //   const projectsMap = projects.reduce((acc, project) => {
  //     acc[project.id] = project;
  //     return acc;
  //   }, {} as Record<string, Project>);
  //
  //   const updatedTasks = tasks.map(task => {
  //     if (task.projectId && projectsMap[task.projectId]) {
  //       return {
  //         ...task,
  //         project: {
  //           id: projectsMap[task.projectId].id,
  //           title: projectsMap[task.projectId].title
  //         }
  //       };
  //     }
  //     return task;
  //   });
  //   setTasksWithProjects(updatedTasks);
  // }, [tasks, projects]);

  // 更新共享的项目列表和时间分组信息 - 使用useCallback创建稳定的更新函数
  const updateSharedState = useCallback(() => {
    // 更新项目列表
    setSortedProjects(projects);

    // 计算可见项目 ID
    if (viewMode === 'project') {
      // 创建项目映射表
      const projectsMap = projects.reduce((acc, project) => {
        acc[project.id] = project;
        return acc;
      }, {} as Record<string, Project>);

      // 获取项目ID列表 - 包括所有项目，不仅仅是有任务的项目
      const projectIds = [...new Set([...Object.keys(tasksByProject), ...projects.map(p => p.id)])];

      // 对项目进行排序
      const sortedProjectIds = [...projectIds].sort((a, b) => {
        const projectA = a === 'none' ? null : projectsMap[a];
        const projectB = b === 'none' ? null : projectsMap[b];

        // 无项目排在最前面（继承默认项目的位置）
        if (a === 'none') return -1;
        if (b === 'none') return 1;

        // 其他项目按原顺序排列
        return 0;
      });

      setVisibleProjectIds(sortedProjectIds);
    }

    // 计算时间分组
    if (viewMode === 'time') {
      // 获取时间分组的键
      const timeGroups = Object.keys(tasksByTime) as Array<keyof typeof tasksByTime>;

      // 过滤掉空的时间组
      const nonEmptyTimeGroups = timeGroups.filter(key => tasksByTime[key].length > 0);

      // 定义时间组的优先级顺序
      const timeGroupOrder: Record<string, number> = {
        today: 1,      // 今天最重要，放在最前面
        tomorrow: 2,   // 其次是明天
        future: 3,     // 然后是未来
        yesterday: 4,  // 接着是昨天
        past: 5,       // 然后是过去
        noDate: 6      // 无日期放在最后
      };

      // 根据优先级排序时间组
      nonEmptyTimeGroups.sort((a, b) => timeGroupOrder[a] - timeGroupOrder[b]);

      setSortedTimeGroups(nonEmptyTimeGroups);
    }
  }, [projects, tasksByProject, tasksByTime, viewMode, setSortedProjects, setVisibleProjectIds, setSortedTimeGroups]);

  // 使用单独的effect调用更新函数以避免循环依赖
  useEffect(() => {
    // 使用startTransition包装状态更新以避免阻塞UI
    startTransition(() => {
      updateSharedState();
    });
  }, [updateSharedState]);

  // 处理视图模式切换 - 使用 useCallback 优化
  const handleViewModeChange = useCallback((id: string) => {
    // 使用 startTransition 包装状态更新，实现 optimistic UI
    console.log(t('v3/tasks:logs.viewModeChanging', { from: viewMode, to: id }));
    startTransition(() => {
      setViewMode(id as TaskViewMode);
      console.log(t('v3/tasks:logs.viewModeUpdated', { to: id }));

      // 立即更新共享状态，不等待下一个渲染周期
      updateSharedState();
      console.log(t('v3/tasks:logs.sharedStateUpdated', { viewMode: id }));
    });
  }, [t, viewMode, setViewMode, updateSharedState]);

  // 处理显示已完成任务切换 - 使用 useCallback 优化
  const toggleShowCompleted = useCallback(() => {
    setShowCompleted(prev => !prev);
  }, [setShowCompleted]);

  // 根据时间组获取对应的日期 - 使用 useMemo 优化
  const getTimeGroupDate = useCallback((groupKey: TimeGroupCategory): Date | null => {
    const today = new Date();

    switch (groupKey) {
      case 'today':
        return startOfDay(today);
      case 'tomorrow':
        return startOfDay(addDays(today, 1));
      case 'yesterday':
        return startOfDay(addDays(today, -1));
      default:
        return null;
    }
  }, []);

  // 处理任务切换 - 使用 useCallback 优化
  const handleToggleTask = useCallback((taskId: string, completed: boolean) => {
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      updateTask({
        id: taskId,
        done: completed,
      });
    }
  }, [tasks, updateTask]);

  // 处理任务点击 - 使用 useCallback 优化
  const handleTaskClick = useCallback((taskId: string) => {
    setSelectedTaskId(taskId);
    setIsTaskDetailOpen(true);
  }, []);

  // 处理关闭任务详情浮层 - 使用 useCallback 优化
  const handleCloseTaskDetail = useCallback(() => {
    setIsTaskDetailOpen(false);
    // 延迟清除任务ID，确保关闭动画完成后再清除
    setTimeout(() => {
      setSelectedTaskId(null);
    }, 300);
  }, []);

  // 处理添加任务 - 使用 useCallback 优化
  const handleAddTask = useCallback(() => {
    setSelectedTaskId(null);
    setNewTaskInitialProjectId(undefined);
    setNewTaskProjectIdAtom(undefined);
    setIsTaskDetailOpen(true);
  }, [setNewTaskProjectIdAtom]);

  // 处理在特定项目中创建任务 - 使用 useCallback 优化
  const handleCreateTaskInProject = useCallback((projectId: string) => {
    // 设置预选项目ID，但不设置任务ID（表示新建任务）
    setSelectedTaskId(null);

    // 如果projectId为空字符串，则视为默认新建任务（不指定项目）
    if (!projectId) {
      setNewTaskInitialProjectId(undefined);
      setNewTaskProjectIdAtom(undefined);
    } else {
      // 否则，设置特定项目ID
      setNewTaskInitialProjectId(projectId);
      setNewTaskProjectIdAtom(projectId);
    }

    // 打开任务详情浮层
    setIsTaskDetailOpen(true);
  }, [setNewTaskProjectIdAtom]);

  // 处理创建任务（支持项目、重要性和时间） - 使用 useCallback 优化
  const handleCreateTask = useCallback((params: CreateTaskParams) => {
    console.log('Creating task with params:', params);

    // 设置任务ID为null（表示新建任务）
    setSelectedTaskId(null);

    // 设置项目ID
    if (params.projectId) {
      setNewTaskInitialProjectId(params.projectId);
      setNewTaskProjectIdAtom(params.projectId);
    } else {
      setNewTaskInitialProjectId(undefined);
      setNewTaskProjectIdAtom(undefined);
    }

    // 设置重要性预设值
    setNewTaskImportance(params.importance);

    // 设置时间组预设值
    setNewTaskTimeGroup(params.timeGroup);

    // 打开任务详情浮层
    setIsTaskDetailOpen(true);
  }, [setNewTaskProjectIdAtom]);

  // 处理任务更新（支持项目、重要性和时间） - 使用 useCallback 优化
  const handleUpdateTask = useCallback((taskId: string, params: UpdateTaskParams) => {
    console.log('Updating task with params:', taskId, params);

    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const updateData: any = { id: taskId };

    // 处理项目更新
    if (params.projectId !== undefined) {
      updateData.projectId = params.projectId;
    }

    // 处理重要性更新
    if (params.importance) {
      updateData.vital = params.importance.vital;
      updateData.urgent = params.importance.urgent;
    }

    // 处理时间更新
    if (params.timeGroup) {
      const date = getTimeGroupDate(params.timeGroup);
      if (date) {
        updateData.startAt = date.toISOString();
      }
    }

    // 更新任务
    updateTask(updateData);
  }, [tasks, updateTask, getTimeGroupDate]);

  // 处理项目编辑 - 使用 useCallback 优化
  const handleEditProject = useCallback((projectId: string) => {
    // 查找项目
    const project = projects.find(p => p.id === projectId);
    if (project) {
      // 设置编辑项目状态
      setEditingProject({
        id: project.id,
        title: project.title,
        description: project.context || '',
        folderId: project.folderId
      });
      // 打开编辑模态框
      setIsEditModalOpen(true);
    }
  }, [projects]);

  // 处理项目点击 - 导航到项目详情页 - 使用 useCallback 优化
  const handleProjectClick = useCallback((projectId: string) => {
    if (projectId !== 'none') {
      navigate(`/v3/projects/${projectId}`, { state: { from: 'tasks' } });
    }
  }, [navigate]);

  // 处理创建新项目 - 使用 useCallback 包装
  const handleCreateProject = useCallback(() => {
    setEditingProject({
      id: '',
      title: '',
      description: '',
      folderId: null
    });
    setIsEditModalOpen(true);
  }, [setEditingProject, setIsEditModalOpen]); // 依赖项应包含所有从外部作用域引用的变量/函数

  // 处理保存项目 - 使用 useCallback 优化
  const handleSaveProject = useCallback(async (title: string, description: string, folderId?: string | null): Promise<boolean> => {
    try {
      console.log('Tasks: handleSaveProject 收到参数:', { title, description, folderId });
      if (editingProject?.id) {
        // 更新现有项目
        const success = await updateProject({
          id: editingProject.id,
          title,
          context: description,
          folderId
        });
        return !!success;
      } else {
        // 创建新项目
        const success = await createProject({
          title,
          context: description,
          folderId
        });
        return !!success;
      }
    } catch (error) {
      console.error(t('v3/common:errors.saveFailed'), error);
      return false;
    }
  }, [editingProject, updateProject, createProject, t]);

  // 关闭编辑模态框 - 使用 useCallback 优化
  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingProject(null);
  }, []);

  // 处理项目编辑模态框中的合并按钮点击 - 使用 useCallback 优化
  const handleMergeClick = useCallback(() => {
    // 关闭编辑模态框，打开合并模态框
    setIsEditModalOpen(false);
    setIsMergeModalOpen(true);
  }, []);

  // 处理项目合并 - 使用 useCallback 优化
  const handleMergeProject = useCallback(async (targetProjectId: string, keepSourceContent: boolean): Promise<boolean> => {
    if (!editingProject) return false;

    try {
      // 调用项目合并服务
      await ProjectService.mergeProjects(
        editingProject.id,
        targetProjectId,
        keepSourceContent
      );

      // 合并成功后，如果当前在项目视图，重新加载项目列表
      if (viewMode === 'project') {
        await fetchProjects();
        // 重新加载任务列表
        await fetchTasks();
      }
      return true;
    } catch (error) {
      console.error(t('v3/projects:errors.mergeFailed'), error);
      return false;
    }
  }, [editingProject, viewMode, fetchProjects, fetchTasks, t]);

  // 关闭合并模态框 - 使用 useCallback 优化
  const handleCloseMergeModal = useCallback(() => {
    setIsMergeModalOpen(false);
    setEditingProject(null);
  }, []);

  // 渲染按类别分组的视图 - 四象限（重要且紧急、重要、紧急、不重要不紧急） - 使用 useMemo 优化
  const renderCategoryView = useMemo(() => {
    // 定义重要性类别
    const importanceCategories: Record<string, ImportanceCategory> = {
      vitalUrgent: { vital: true, urgent: true, label: t('v3/tasks:categories.vitalUrgent') },
      vital: { vital: true, urgent: false, label: t('v3/tasks:categories.vital') },
      urgent: { vital: false, urgent: true, label: t('v3/tasks:categories.urgent') },
      other: { vital: false, urgent: false, label: t('v3/tasks:categories.other') }
    };

    return (
      <>
        {/* 重要且紧急 */}
        <DroppableArea
          areaId="vital-urgent"
          data={{
            type: 'importance',
            importance: importanceCategories.vitalUrgent
          }}
          highlightColor="bg-ios-red/10 dark:bg-ios-dark-red/20"
        >
          <TaskList
            tasks={vitalUrgentTasks}
            title={t('v3/tasks:categories.vitalUrgent')}
            color="text-ios-red dark:text-ios-dark-red"
            id="vital-urgent"
            icon={
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 15H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 11V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10.29 3.86001L1.82 18C1.64537 18.3024 1.55296 18.6453 1.55199 18.9945C1.55101 19.3437 1.6415 19.6871 1.81442 19.9905C1.98734 20.2939 2.23672 20.5468 2.53771 20.7239C2.83869 20.901 3.1808 20.9962 3.53 21H20.47C20.8192 20.9962 21.1613 20.901 21.4623 20.7239C21.7633 20.5468 22.0127 20.2939 22.1856 19.9905C22.3585 19.6871 22.449 19.3437 22.448 18.9945C22.447 18.6453 22.3546 18.3024 22.18 18L13.71 3.86001C13.5317 3.56611 13.2807 3.32313 12.9812 3.15449C12.6817 2.98585 12.3437 2.89726 12 2.89726C11.6563 2.89726 11.3183 2.98585 11.0188 3.15449C10.7193 3.32313 10.4683 3.56611 10.29 3.86001Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
            onToggleTask={handleToggleTask}
            onTaskClick={handleTaskClick}
          />
        </DroppableArea>

        {/* 重要 */}
        <DroppableArea
          areaId="vital"
          data={{
            type: 'importance',
            importance: importanceCategories.vital
          }}
          highlightColor="bg-ios-orange/10 dark:bg-ios-dark-orange/20"
        >
          <TaskList
            tasks={vitalTasks}
            title={t('v3/tasks:categories.vital')}
            color="text-ios-orange dark:text-ios-dark-orange"
            id="vital"
            icon={
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 15H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 11V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10.29 3.86001L1.82 18C1.64537 18.3024 1.55296 18.6453 1.55199 18.9945C1.55101 19.3437 1.6415 19.6871 1.81442 19.9905C1.98734 20.2939 2.23672 20.5468 2.53771 20.7239C2.83869 20.901 3.1808 20.9962 3.53 21H20.47C20.8192 20.9962 21.1613 20.901 21.4623 20.7239C21.7633 20.5468 22.0127 20.2939 22.1856 19.9905C22.3585 19.6871 22.449 19.3437 22.448 18.9945C22.447 18.6453 22.3546 18.3024 22.18 18L13.71 3.86001C13.5317 3.56611 13.2807 3.32313 12.9812 3.15449C12.6817 2.98585 12.3437 2.89726 12 2.89726C11.6563 2.89726 11.3183 2.98585 11.0188 3.15449C10.7193 3.32313 10.4683 3.56611 10.29 3.86001Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
            onToggleTask={handleToggleTask}
            onTaskClick={handleTaskClick}
          />
        </DroppableArea>

        {/* 紧急 */}
        <DroppableArea
          areaId="urgent"
          data={{
            type: 'importance',
            importance: importanceCategories.urgent
          }}
          highlightColor="bg-ios-blue/10 dark:bg-ios-dark-blue/20"
        >
          <TaskList
            tasks={urgentTasks}
            title={t('v3/tasks:categories.urgent')}
            color="text-ios-blue dark:text-ios-dark-blue"
            id="urgent"
            icon={
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
            onToggleTask={handleToggleTask}
            onTaskClick={handleTaskClick}
          />
        </DroppableArea>

        {/* 不重要不紧急 */}
        <DroppableArea
          areaId="other"
          data={{
            type: 'importance',
            importance: importanceCategories.other
          }}
          highlightColor="bg-ios-gray/10 dark:bg-ios-dark-gray/20"
        >
          <TaskList
            tasks={otherTasks}
            title={t('v3/tasks:categories.other')}
            color="text-ios-gray dark:text-ios-dark-gray"
            id="other"
            icon={
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 11L12 14L20 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20 12V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6C4 4.89543 4.89543 4 6 4H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
            onToggleTask={handleToggleTask}
            onTaskClick={handleTaskClick}
          />
        </DroppableArea>

        {/* 已完成任务 */}
        <TaskList
          tasks={completedTasks}
          title={t('v3/tasks:categories.completed')}
          color="text-ios-green dark:text-ios-dark-green"
          id="completed"
          icon={
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          onToggleTask={handleToggleTask}
          onTaskClick={handleTaskClick}
        />
      </>
    );
  }, [t, vitalUrgentTasks, vitalTasks, urgentTasks, otherTasks, completedTasks, handleToggleTask, handleTaskClick]);

  // 渲染按时间分组的视图 - 使用 useMemo 优化
  const renderTimeView = useMemo(() => {
    // 获取时间分组的键
    const timeGroups = Object.keys(tasksByTime) as Array<keyof typeof tasksByTime>;

    // 定义可拖放的时间组（只支持昨天、今天、明天）
    const droppableTimeGroups: TimeGroupCategory[] = ['yesterday', 'today', 'tomorrow'];

    // 过滤时间组：保留非空组和可拖放组
    const nonEmptyTimeGroups = timeGroups.filter(key =>
      tasksByTime[key].length > 0 || droppableTimeGroups.includes(key as TimeGroupCategory)
    );

    // 定义时间组的优先级顺序
    const timeGroupOrder: Record<string, number> = {
      today: 1,      // 今天最重要，放在最前面
      tomorrow: 2,   // 其次是明天
      future: 3,     // 然后是未来
      yesterday: 4,  // 接着是昨天
      past: 5,       // 然后是过去
      noDate: 6      // 无日期放在最后
    };

    // 根据优先级排序时间组
    nonEmptyTimeGroups.sort((a, b) => timeGroupOrder[a] - timeGroupOrder[b]);

    // 时间组的图标和颜色映射
    const timeGroupIcons: Record<string, { icon: React.ReactNode, color: string, highlightColor: string }> = {
      today: {
        icon: (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="15" r="2" fill="currentColor"/>
          </svg>
        ),
        color: "text-ios-red dark:text-ios-dark-red",
        highlightColor: "bg-ios-red/10 dark:bg-ios-dark-red/20"
      },
      tomorrow: {
        icon: (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ),
        color: "text-ios-orange dark:text-ios-dark-orange",
        highlightColor: "bg-ios-orange/10 dark:bg-ios-dark-orange/20"
      },
      yesterday: {
        icon: (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ),
        color: "text-ios-gray dark:text-ios-dark-gray",
        highlightColor: "bg-ios-gray/10 dark:bg-ios-dark-gray/20"
      },
      future: {
        icon: (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ),
        color: "text-ios-blue dark:text-ios-dark-blue",
        highlightColor: "bg-ios-blue/10 dark:bg-ios-dark-blue/20"
      },
      past: {
        icon: (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 8V12L9 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ),
        color: "text-ios-gray dark:text-ios-dark-gray",
        highlightColor: "bg-ios-gray/10 dark:bg-ios-dark-gray/20"
      },
      noDate: {
        icon: (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 11L12 14L20 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M20 12V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6C4 4.89543 4.89543 4 6 4H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ),
        color: "text-ios-gray dark:text-ios-dark-gray",
        highlightColor: "bg-ios-gray/10 dark:bg-ios-dark-gray/20"
      }
    };

    // 时间组的标题映射
    const timeGroupTitles: Record<string, string> = {
      today: t('v3/tasks:categories.today'),
      tomorrow: t('v3/tasks:categories.tomorrow'),
      yesterday: t('v3/tasks:categories.yesterday'),
      future: t('v3/tasks:categories.future'),
      past: t('v3/tasks:categories.past'),
      noDate: t('v3/tasks:categories.noDate')
    };

    return (
      <>
        {nonEmptyTimeGroups.map(groupKey => {
          const isDroppable = droppableTimeGroups.includes(groupKey as TimeGroupCategory);

          // 如果是可拖放的时间组，使用DroppableArea包装
          if (isDroppable) {
            return (
              <DroppableArea
                key={groupKey}
                areaId={groupKey}
                data={{
                  type: 'time',
                  timeGroup: groupKey as TimeGroupCategory
                }}
                highlightColor={timeGroupIcons[groupKey].highlightColor}
              >
                <TaskList
                  tasks={tasksByTime[groupKey]}
                  title={timeGroupTitles[groupKey]}
                  color={timeGroupIcons[groupKey].color}
                  icon={timeGroupIcons[groupKey].icon}
                  id={groupKey}
                  onToggleTask={handleToggleTask}
                  onTaskClick={handleTaskClick}
                  isTimeGroup={true}
                  timeGroupDate={getTimeGroupDate(groupKey as TimeGroupCategory)}
                />
              </DroppableArea>
            );
          }

          // 否则，直接渲染TaskList
          return (
            <TaskList
              key={groupKey}
              tasks={tasksByTime[groupKey]}
              title={timeGroupTitles[groupKey]}
              color={timeGroupIcons[groupKey].color}
              icon={timeGroupIcons[groupKey].icon}
              id={groupKey}
              onToggleTask={handleToggleTask}
              onTaskClick={handleTaskClick}
            />
          );
        })}
      </>
    );
  }, [tasksByTime, t, handleToggleTask, handleTaskClick, getTimeGroupDate]);

  // 渲染按项目分组的视图 - 使用 useMemo 优化
  const renderProjectView = useMemo(() => {
    // 创建项目映射表，用于显示项目名称
    const projectsMap = projects.reduce((acc, project) => {
      acc[project.id] = project;
      return acc;
    }, {} as Record<string, Project>);

    // 获取项目ID列表 - 包括所有项目，不仅仅是有任务的项目
    const projectIds = [...new Set([...Object.keys(tasksByProject), ...projects.map(p => p.id)])];

    // 对项目进行排序，确保无项目始终在最前面
    const sortedProjectIds = [...projectIds].sort((a, b) => {
      // 无项目排在最前面（继承默认项目的位置）
      if (a === 'none') return -1;
      if (b === 'none') return 1;

      // 其他项目按原顺序排列
      return 0;
    });

    // 添加"新建项目"按钮组件 - 使用 React.memo 包装
    const AddProjectButton = React.memo(() => (
      <div className="px-4 mb-6 mt-2">
        <button
          onClick={handleCreateProject} // 现在 handleCreateProject 是稳定的
          className="w-full py-3 flex items-center justify-center bg-white dark:bg-ios-dark-gray5 rounded-ios-lg shadow-ios dark:shadow-ios-dark text-ios-blue dark:text-ios-dark-blue"
        >
          <span className="mr-2">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
            </svg>
          </span>
          <span className="text-ios-body font-medium">{t('v3/projects:actions.createProject')}</span>
        </button>
      </div>
    ));

    return (
      <>
        {sortedProjectIds.map(projectId => {
          const project = projectId === 'none' ? null : projectsMap[projectId];
          const projectName = project ? project.title : t('v3/tasks:categories.noProject');
          const projectDescription = project?.context || '';
          // 无项目使用特殊颜色和图标（继承默认项目的样式）
          const projectColor = projectId === 'none'
            ? 'text-ios-blue dark:text-ios-dark-blue'
            : 'text-ios-gray dark:text-ios-dark-gray';
          const tasks = tasksByProject[projectId] || [];

          // 为无项目和其他项目使用不同的图标
          const projectIcon = projectId === 'none' ? (
            // Inbox 图标（原默认项目图标）
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 12H16L14 15H10L8 12H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M5.45 5.11L2 12V18C2 18.5304 2.21071 19.0391 2.58579 19.4142C2.96086 19.7893 3.46957 20 4 20H20C20.5304 20 21.0391 19.7893 21.4142 19.4142C21.7893 19.0391 22 18.5304 22 18V12L18.55 5.11C18.3844 4.77679 18.1292 4.49637 17.813 4.30028C17.4967 4.10419 17.1321 4.0002 16.76 4H7.24C6.86792 4.0002 6.50326 4.10419 6.18704 4.30028C5.87083 4.49637 5.61558 4.77679 5.45 5.11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            // 普通项目图标
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          );

          const actualProjectId = projectId !== 'none' ? projectId : undefined;

          return (
            <DroppableArea
              key={projectId}
              areaId={projectId}
              data={{
                type: 'project',
                projectId: projectId
              }}
              isActive={activeProjectId === projectId}
              highlightColor="bg-ios-blue/10 dark:bg-ios-dark-blue/20"
            >
              <TaskList
                tasks={tasks}
                hideProjectName={true}
                title={projectName}
                description={projectDescription}
                projectId={actualProjectId}
                color={projectColor}
                icon={projectIcon}
                id={projectId}
                onToggleTask={handleToggleTask}
                onTaskClick={handleTaskClick}
                onEditProject={handleEditProject}
                onTitleClick={actualProjectId ? () => handleProjectClick(actualProjectId) : undefined}
                isTimeGroup={false}
              />
            </DroppableArea>
          );
        })}
        <AddProjectButton />
      </>
    );
  }, [projects, tasksByProject, t, activeProjectId, handleToggleTask, handleTaskClick, handleEditProject, handleProjectClick, handleCreateProject]);

  // 渲染空状态 - 使用 useMemo 优化
  const renderEmptyState = useMemo(() => {
    return (
      <div className="flex flex-col items-center justify-center py-12 animate-ios-fade-in">
        <div className="w-16 h-16 text-ios-gray3 dark:text-ios-dark-gray3">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 11L12 14L20 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M20 12V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6C4 4.89543 4.89543 4 6 4H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <p className="mt-4 text-ios-body text-ios-gray dark:text-ios-dark-gray">
          {searchQuery ? t('v3/tasks:emptyState.noMatchingTasks') : t('v3/tasks:emptyState.title')}
        </p>
        {!searchQuery && (
          <button
            onClick={handleAddTask}
            className="mt-4 px-4 py-2 bg-ios-blue dark:bg-ios-dark-blue text-white rounded-full text-ios-subhead font-medium"
          >
            {t('v3/tasks:actions.createTask')}
          </button>
        )}
        {/* 添加额外的空白区域，确保页面有足够的内容可以滚动 */}
        <div className="h-60"></div>
      </div>
    );
  }, [searchQuery, t, handleAddTask]);

  return (
    <TaskDndContext
      onCreateTask={handleCreateTask}
      onUpdateTask={handleUpdateTask}
    >
      <div className="bg-ios-gray6 dark:bg-ios-dark-gray6">

        {/* 导航栏 */}
        <TasksNavigationBar
          title={t('v3/tasks:title')}
          largeTitleMode={true}
        />

        {/* 可滚动内容区域 */}
        <div
          ref={containerRef}
          className="flex-1 overflow-y-auto pb-[calc(4rem+env(safe-area-inset-bottom))] overscroll-bounce"
          style={{ overscrollBehavior: 'contain' }}
        >

        {/* 搜索栏 - 始终可见 */}
        <div
          className="flex items-center px-4 py-2 overflow-hidden mt-2"
          style={{
            marginBottom: '0.5rem'
          }}
        >
          <div className="flex-grow pt-2 pb-2 w-full"> {/* 添加内边距确保搜索框有足够的空间 */}
            <TaskSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={t('v3/tasks:taskForm.searchPlaceholder')}
            />
          </div>
        </div>

        {/* 分段控制器和显示已完成按钮 */}
        <div className="px-4 mb-4 flex items-center">
          <div className="flex-grow">
            <IOSSegmentedControl
              options={[
                { id: 'project', label: t('v3/tasks:viewModes.project') },
                { id: 'time', label: t('v3/tasks:viewModes.time') },
                { id: 'category', label: t('v3/tasks:viewModes.category') }
              ]}
              selectedId={viewMode}
              onChange={handleViewModeChange}
              className="w-full"
            />
          </div>
          {/* 显示已完成按钮 - iOS风格列表图标 */}
          <button
            onClick={toggleShowCompleted}
            className="flex items-center justify-center w-10 h-10 ml-3 text-ios-blue dark:text-ios-dark-blue"
            aria-label={showCompleted ? t('v3/tasks:actions.hideCompleted') : t('v3/tasks:actions.showCompleted')}
          >
            <div className="w-6 h-6">
              {showCompleted ? <ListViewIcon /> : <ListViewHiddenIcon />}
            </div>
          </button>
        </div>

        {/* NOTE: 显示/隐藏已完成任务是常用功能，不应放入二级菜单，保持在主界面中 */}

        <div>
          {isLoading && tasks.length === 0 ? (
            // 加载状态
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-ios-blue dark:border-ios-dark-blue border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-ios-body text-ios-gray dark:text-ios-dark-gray">{t('v3/common:loadingText')}</p>
            </div>
          ) : filteredTasks.length === 0 ? (
            // 空状态
            renderEmptyState
          ) : (
            // 任务列表 - 根据当前视图模式渲染
            <AnimatePresence mode="wait">
              <motion.div
                key={viewMode}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="pb-4"
              >
                {viewMode === 'category' && renderCategoryView}
                {viewMode === 'time' && renderTimeView}
                {viewMode === 'project' && renderProjectView}

                {/* 添加额外的空白区域，确保页面有足够的内容可以滚动 */}
                <div className="h-40"></div>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>

      {/* 添加项目按钮 */}
      {viewMode === 'project' && <ProjectActionButton onClick={handleCreateProject} />}

      {/* 添加任务按钮 - 可拖拽 */}
      <DraggableTaskButton onClick={handleAddTask} />

      {/* 项目编辑/创建模态框 */}
      {editingProject && (
        <ProjectEditModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          onSave={handleSaveProject}
          onMergeClick={editingProject.id ? handleMergeClick : undefined}
          projectId={editingProject.id}
          initialTitle={editingProject.title}
          initialDescription={editingProject.description}
          initialFolderId={editingProject.folderId}
          isCreating={!editingProject.id}
        />
      )}

      {/* 项目合并模态框 */}
      {editingProject && (
        <ProjectMergeModal
          isOpen={isMergeModalOpen}
          onClose={handleCloseMergeModal}
          onMerge={handleMergeProject}
          sourceProjectId={editingProject.id}
          sourceProjectTitle={editingProject.title}
          projects={projects}
        />
      )}

      {/* 任务详情浮层 */}
      <TaskDetailSheet
        taskId={selectedTaskId}
        projectId={newTaskInitialProjectId}
        importance={newTaskImportance}
        timeGroup={newTaskTimeGroup}
        isOpen={isTaskDetailOpen}
        onClose={handleCloseTaskDetail}
      />
    </div>
    </TaskDndContext>
  );
};
